import { BaseControl } from '@Common/Controls/BaseControl';
import { ContextMenu } from '@Common/Controls/ContextMenu';
import { DropDown } from '@Common/Controls/DropDown';
import { ITabControlParameters } from '@Common/Controls/Tabs/ITabControlParameters';
import { TabFocusAssistant } from '@Common/Controls/Tabs/TabFocusAssistant';
import { ITabItem, TabItem } from '@Common/Controls/Tabs/TabItem';
import constants from '@Common/Core/Constant';
import { contextMenuItems } from '@Common/Core/ContextMenuItems';
import { safeDestroys } from '@Common/Core/Destroyable';
import Guid from '@Common/Core/Guid';
import { MulticastDelegateBag } from '@Common/Core/MulticastDelegate';
import { findBy } from '@Common/Helpers/CollectionUtils';
import { Errors } from '@Common/Helpers/Errors';
import urlHelper from '@Common/Helpers/UrlHelper';

type CreateTabOption = {
    id?: string;
    index?: number;
    liControl?: JQuery;
    addedTab?: boolean;
};

export class Tabs extends BaseControl<ITabControlParameters> {
    private static readonly defaultSetting: ITabControlParameters = {
        css: null,
        className: '',
        width: 'auto',
        height: 'auto',

        headerClass: '',
        headerCss: '',

        items: [],
        hasPlus: false,
        sortable: false,
        itemDefaultStyle: constants.tabDefaultStyle.itemDefaultStyle,
        itemHoverStyle: constants.tabDefaultStyle.itemHoverStyle,
        itemSelectedStyle: constants.tabDefaultStyle.itemSelectedStyle,
        sortComparer: null,
        allowClose: false,
        contentStyle: {},

        headerIconSize: 24,

        maxCaptionWidth: null,

        tabsDropDown: false,
        adjustHeader: false,
        prependTabs: false,
        allowSort: false
    };

    private readonly events = new MulticastDelegateBag();

    public readonly onActivateTab = this.events.create<(current: TabItem, prev: TabItem | null) => void>();
    public readonly onToggleTab = this.events.create<(current: ITabItem, prev?: ITabItem | null) => boolean | void>();
    public readonly onPlus = this.events.create<IHandler>();
    public readonly onClose = this.events.create<ICallback<ITabItem>>();

    private readonly tabs: TabItem[] = [];
    private readonly header: JQuery<HTMLElement>;
    private readonly dropDownTabs: DropDown;
    private readonly plusButton: JQuery;
    private readonly content: JQuery<HTMLElement>;
    private readonly focusAssistant: TabFocusAssistant;
    private active: TabItem | null = null;

    constructor(param?: ITabControlParameters) {
        super(_.defaults(param, Tabs.defaultSetting));
        this._root = $('<div class="tabs"/>').css('height', this._param.height).addClass(this._param.className);

        if (this._param.css) {
            this._root.css(this._param.css);
        }

        this.header = $('<ul class="tab-header"/>').appendTo(this._root);
        this.header.addClass(this._param.headerClass);

        this.focusAssistant = new TabFocusAssistant(this.header);

        if (this._param.allowSort) {
            const tempHidden = [];

            this.header.sortable({
                axis: 'x',
                revert: true,
                cancel: 'span.close, li.drop-down-tabs',
                helper: 'clone',
                distance: 20,
                start: (): void => {
                    const tabsOffset = this.header.offset().top + this.header.height();
                    _.forEach(this.tabs, (tab): void => {
                        if (tabsOffset < tab.li.offset().top) {
                            tab.li.hide();
                            tempHidden.push(tab);
                        }
                    });
                },
                stop: (): void => {
                    _.forEach(tempHidden, (tab): void => {
                        tab.li.show();
                    });
                    tempHidden.length = 0;

                    // sort drop down
                    if (this._param.tabsDropDown && !this._param.sortable) {
                        const newDropDownItems = [];
                        _(this.header.children('li'))
                            .filter(i => !$(i).hasClass('drop-down-tabs'))
                            .forEach((i): void => {
                                const data = $(i).data('item');
                                if (data.id) {
                                    const d = findBy(this.dropDownTabs.items(), data.id);
                                    d && newDropDownItems.push(d);
                                }
                            });
                        this.dropDownTabs.items(newDropDownItems.reverse());
                    }
                }
            });
        }

        if (this._param.headerCss) {
            this.header.css(this._param.headerCss);
        }

        if (this._param.hasPlus) {
            $('<li class="plus-button"><a href="#"></a></li>').appendTo(this.header);
        }

        this.plusButton = this._root.find('.plus-button');
        this.plusButton.on('click', (e): void => {
            e.preventDefault();
            this.onPlus();
        });

        this.dropDownTabs = new DropDown({
            width: 18,
            cloneItems: false,
            sortable: this._param.sortable
        });

        this.dropDownTabs.onShowContent(function () {
            // HACK: use internal dd state _root,_popup
            const left = this._root.offset().left - (this._popup.getControl().width() - this._root.outerWidth());
            this._popup.getControl().css({ left });
        });

        this.dropDownTabs.onItemClick((item): void => {
            if (item?.tab) {
                this.toggleTab(item.tab);
            }
        });

        if (this._param.tabsDropDown) {
            $('<li class="drop-down-tabs"></li>').append(this.dropDownTabs.getControl().hide()).appendTo(this.header);
        }

        this.content = $('<div class="tab-content"/>');
        this.content.css(this._param.contentStyle);
        this.content.appendTo(this._root);

        if (this._param.items && this._param.items.length > 0 && !_.find(this._param.items, (i): any => i.isActive)) {
            this._param.items[0].isActive = true;
        }

        this._param.sortable && this._param.items && this._param.sortComparer && this._param.items.sort(this._param.sortComparer);

        for (const item of this._param.items) {
            this.createTab(item);
        }

        this.needVisibly();
    }

    public createTab(item: ITabItem, tabParam?: CreateTabOption): void {
        tabParam = $.extend(
            {
                liControl: null,
                id: null,
                addedTab: false
            },
            tabParam
        );

        const isNew = tabParam.id == null;
        const isActive = item.isActive === true;
        item.id = item.id || Guid.newGuid();

        item.itemDefaultStyle ??= '';
        item.itemHoverStyle ??= '';
        item.itemSelectedStyle ??= '';

        const name = $('<span class="name text-no-wrap" />').text(item.name);
        const li = (tabParam.liControl ?? $('<li/>'))
            .empty()
            .off()
            .append(name)
            .data('item', item)
            .on('click', (e): void => {
                if (item.disabled !== true && !$(e.target).hasClass('close')) {
                    const tab = this.getTab(item.id);
                    this.toggleTab(tab);
                }
            });

        if (this._param.maxCaptionWidth) {
            li.css('max-width', this._param.maxCaptionWidth).addClass('width-limited');
        }

        if (item.tooltip) {
            li.attr('title', item.tooltip);
        }

        let icon;

        if (item.icon) {
            icon = $('<span class="icon"/>');
            li.prepend(icon);
            icon.css('backgroundImage', urlHelper.getCssImageUrl(item.icon));
        }

        if (isNew) {
            // styles
            if (isActive) {
                li.addClass([this._param.itemSelectedStyle, item.itemSelectedStyle]);
            } else {
                li.addClass([this._param.itemDefaultStyle, item.itemDefaultStyle]);
            }
        }

        // append tab to tabs array
        let newTab: TabItem;
        if (tabParam.liControl) {
            const index = _.findIndex(this.tabs, (tab): boolean => tab.id === tabParam.id);
            if (index >= 0) {
                newTab = this.tabs[index] = new TabItem(li, item, name, icon);
            }
        } else {
            this.tabs.push((newTab = new TabItem(li, item, name, icon)));
        }

        if (item.allowClose === true || (this._param.allowClose && item.allowClose !== false)) {
            $('<span class="close"/>')
                .on('click', (): void => {
                    this.onClose(item);
                })
                .appendTo(li.addClass('allow-close'));
            this.bindContextMenu(newTab);
        }

        // hover
        li.on('mouseenter', () => {
            const isSelected = li.attr('selected');

            if (!isSelected) {
                li.removeClass(this._param.itemDefaultStyle).removeClass(this._param.itemSelectedStyle).addClass(this._param.itemHoverStyle);

                li.removeClass(item.itemDefaultStyle).removeClass(item.itemSelectedStyle).addClass(item.itemHoverStyle);
            }
        }).on('mouseleave', () => {
            const isSelected = li.attr('selected');

            if (!isSelected) {
                li.removeClass(this._param.itemHoverStyle).addClass(this._param.itemDefaultStyle);
                li.removeClass(item.itemHoverStyle).addClass(item.itemDefaultStyle);
            }
        });

        // append tab to header
        if (this._param.sortable && this._param.sortComparer && tabParam.addedTab) {
            const sortItems = [...this.tabs].sort((a, b) => this._param.sortComparer(a.item, b.item));
            const index = sortItems.findIndex(st => st.item === item);
            sortItems[index].li.appendTo(this.header);
            this.header.children().eq(index).before(sortItems[index].li);
        } else {
            if (tabParam.addedTab) {
                if (_.isNumber(tabParam.index)) {
                    li.insertBefore(this.tabs[tabParam.index].li);
                } else if (this._param.prependTabs) {
                    this.focusAssistant.stopAnimation();
                    li.prependTo(this.header);
                } else {
                    li.appendTo(this.header);
                }
            } else {
                !tabParam.liControl && (this._param.hasPlus ? li.insertBefore(this.plusButton) : li.appendTo(this.header));
            }
        }

        //add item to the DD
        if (tabParam.liControl) {
            this.dropDownTabs.updateItem(tabParam.id, {
                name: item.name,
                id: item.id,
                image: item.icon,
                tab: newTab
            });
        } else {
            tabParam.index
                ? this.dropDownTabs.insertItem(tabParam.index, {
                    name: item.name,
                    id: item.id,
                    image: item.icon,
                    tab: newTab
                })
                : this.dropDownTabs.appendItems([
                    {
                        name: item.name,
                        id: item.id,
                        image: item.icon,
                        tab: newTab
                    }
                ]);
        }

        isNew && isActive && this.toggleTab(newTab);
        this.adjustHeader();
        this.focusAssistant.resumeAnimation();
    }

    public bindContextMenu(tab: TabItem): void {
        //TODO: bind context menu to header
        tab.li.contextMenuAction(coordinates => {
            const menuItems = [contextMenuItems.closeCurrent, contextMenuItems.closeAll];
            if (this.tabs.length > 1) {
                menuItems.push(contextMenuItems.closeAllExceptCurrent);
            }

            const contextMenu = ContextMenu(coordinates);
            contextMenu.setValue(menuItems.map(item => ({ item })));
            contextMenu.itemClick(cmItem => {
                const actualTabs = [...this.tabs];
                if (cmItem.id === contextMenuItems.closeAll.id) {
                    actualTabs.forEach(i => this.onClose(i.item));
                } else if (cmItem.id === contextMenuItems.closeAllExceptCurrent.id) {
                    this.toggleTab(tab);
                    actualTabs.filter(i => !i.li.attr('selected')).forEach(i => this.onClose(i.item));
                } else if (cmItem.id === contextMenuItems.closeCurrent.id) {
                    this.onClose(tab.item);
                }
            });
        });
    }

    public toggleTab(tab: TabItem): void {
        // lazy Load
        const allowSwitch = this.onToggleTab(tab.item, this.active?.item ?? null);

        if (allowSwitch !== false) {
            const oldTab = this.active;
            const newTabId = tab.item.id;
            const oldTabId = oldTab?.item.id;

            this.active = tab;

            if (oldTab?.item) {
                this.content.removeClass(oldTab.item.className);
            }

            if (tab.item) {
                this.content.addClass(tab.item.className);
            }

            // if we have controls as a content
            if (tab.content && tab.content.getControl) {
                const w = this.content.width();
                const h = this.content.height();

                // Bug #976160: Error at loading component in inactive tab in Studio
                if (
                    oldTab &&
                    oldTab.content &&
                    oldTab.content.getControl &&
                    oldTab.content.getControl() &&
                    _.isFunction(oldTab.content.onLoadComplete) &&
                    newTabId &&
                    oldTabId &&
                    newTabId !== oldTabId
                ) {
                    const oldControl = oldTab.content.getControl();
                    oldControl && oldControl.hide();
                    oldTab.content.onLoadComplete().then((): void => {
                        const ctrl = oldTab.content.getControl();
                        if (ctrl && (!this.active || this.active.item !== oldTab.item)) {
                            ctrl.detach().show();
                        }
                    });
                } else {
                    this.content.children().detach();
                }

                const control: JQuery = tab.content.getControl();
                control.appendTo(this.content);
                control.toggle(true);
                tab.content.adjust && tab.content.adjust(w, h);
            } else {
                this.content.children().detach();
                tab.content && tab.content.appendTo(this.content);
            }

            this.header
                .find('li')
                .removeClass(this._param.itemSelectedStyle)
                .removeClass(tab.item.itemSelectedStyle)
                .addClass(this._param.itemDefaultStyle)
                .addClass(tab.item.itemDefaultStyle)
                .removeAttr('selected');

            tab.li.removeClass(this._param.itemHoverStyle);
            tab.li.removeClass(this._param.itemDefaultStyle).removeClass(tab.item.itemDefaultStyle);
            tab.li.addClass(this._param.itemSelectedStyle).addClass(tab.item.itemSelectedStyle);
            tab.li.attr('selected', 'selected');

            this.dropDownTabs.selectItemsById([tab.item.id]);

            // indicate item out of header
            if (this.header.offset().top + this.header.outerHeight() < tab.li.offset().top) {
                tab.li.prependTo(this.header);
            }

            this.focusAssistant.ensureTabVisibility(tab);

            this.onActivateTab(tab, oldTab);
        }
    }

    public isTabsControl(): boolean {
        return true;
    }

    // tslint:disable: lines-between-class-members
    public getTab(id: string): TabItem | undefined;
    public getTab(id: string, throwable: true): TabItem;
    public getTab(id: string, throwable: false): TabItem | undefined;
    public getTab(id: string, throwable = false): TabItem | undefined {
        const tabItem = findBy(this.tabs, id, undefined);
        if (tabItem === undefined && throwable) {
            Errors.invalidOperation('Tabs::getTab', `Cannot find tab with id = ${id}`);
        }
        return tabItem;
    }

    // tslint:enable: lines-between-class-members

    public removeTab(id: string): void {
        const removingTab = this.getTab(id);
        if (removingTab) {
            const wasSelected = removingTab.li.attr('selected');
            removingTab.destroy();
            _.remove(this.tabs, tab => tab.id === id);

            if (wasSelected) {
                this.active = null;
                if (this.tabs.length > 0) {
                    this.tabs[0].li
                        .removeClass(this._param.itemHoverStyle)
                        .removeClass(this._param.itemDefaultStyle)
                        .addClass(this._param.itemSelectedStyle);

                    this.toggleTab(this.tabs[0]);
                }
            }

            this.dropDownTabs.removeItems(item => item.id === id);
        }

        this.needVisibly();
        this.adjustHeader();
    }

    public contains(tabId: string): boolean {
        const tab = _.find(this.tabs, i => i.item.id === tabId);
        return !!tab;
    }

    public setActiveTab(tabId: string): void {
        const tab = _.find(this.tabs, i => i.item.id === tabId);

        tab && this.toggleTab(tab);
    }

    public getActiveTab(): TabItem | null {
        return this.tabs.find(i => i.li.attr('selected')) ?? null;
    }

    public getContentSize(): ISize {
        return {
            width: this.content.width(),
            height: this.content.height()
        };
    }

    public dirty(id: string, isDirty: boolean): void {
        const tab = this.getTab(id, true);
        tab.li.toggleClass('dirty', isDirty === true);
        this.adjustHeader();
    }

    public updateTab(id: string, newTab: ITabItem): void {
        const tab = this.getTab(id, true);
        this.createTab(newTab, { id: id, liControl: tab.li });
    }

    public enableAll(): void {
        for (const tab of this.tabs) {
            tab.enabled(true);
        }
    }

    public disableTab(id: string): void {
        this.enableAll();
        const tab = this.getTab(id);
        tab?.enabled(false);
    }

    public setTabState(id: string, enabled: boolean): void {
        const tab = this.getTab(id);
        tab?.enabled(enabled);
    }

    public disableAllExceptCurrent(): void {
        for (const tab of this.tabs) {
            if (tab !== this.active) {
                tab.enabled(false);
            } else {
                tab.enabled(true);
            }
        }
    }

    public updateTabName(id: string, newName: string, tooltip?: string): void {
        const tab = this.getTab(id);
        if (tab) {
            tab.setName(newName, tooltip);
            this.dropDownTabs.updateItem(id, {
                name: tab.item.name,
                id: tab.item.id,
                image: tab.item.icon,
                tab: tab,
                tooltip: tooltip || tab.item.name
            });
        }
    }

    public adjust(width: number, height: number): void {
        if (width) {
            this._root.outerWidth(width);
            this.content.outerWidth(width);
        }

        if (height) {
            this._root.outerHeight(height);
            this.content.outerHeight(height - this.header.outerHeight());
        }

        if (this._root.elementInDOM()) {
            const activeContent = this.active?.content;
            if (activeContent != null && Function.isFunction(activeContent.adjust)) {
                const contentSize = this.getContentSize();
                activeContent.adjust(contentSize.width, contentSize.height);
            }
            this.adjustHeader();
        }
    }

    public spinnerStart(id: string): void {
        const tab = this.getTab(id) ?? this.getActiveTab();
        tab?.spinnerStart();
    }

    public spinnerStop(id: string): void {
        const tab = this.getTab(id) ?? this.getActiveTab();
        tab?.spinnerStop();
    }

    public adjustHeader(): void {
        const hasTabsInHeader = this.tabs && this.tabs.length && this.header;

        if (this._param.adjustHeader && hasTabsInHeader) {
            for (const tab of this.tabs) {
                tab.name.outerWidth('auto');
            }

            const headerWidth = this.header.width();
            const allTabsWidth = _.sumBy(this.tabs, tab => tab.li.outerWidth(true));

            if (headerWidth > 0 && allTabsWidth > 0 && allTabsWidth > headerWidth) {
                const tabMargin = 5;
                let adjustTabWidth = Math.floor((headerWidth - tabMargin * (this.tabs.length - 1)) / this.tabs.length);
                const smallTabs = [];
                const bigTabs = [];
                let diff = 0;

                for (const tab of this.tabs) {
                    const tabWidth = tab.li.outerWidth(true);
                    if (tabWidth < adjustTabWidth) {
                        smallTabs.push(tab);
                        diff += adjustTabWidth - tabWidth;
                    } else {
                        bigTabs.push(tab);
                    }
                }

                if (diff && smallTabs.length) {
                    adjustTabWidth = Math.floor(adjustTabWidth + diff / bigTabs.length);
                }

                for (const tab of bigTabs) {
                    if (tab.li.outerWidth() > adjustTabWidth) {
                        const tabPadding = this._param.allowClose || tab.allowClose ? 20 : 40;
                        let width = adjustTabWidth - tabPadding;
                        if (this._param.allowClose) {
                            width -= this._param.headerIconSize;
                        }
                        if (tab.icon) {
                            width -= this._param.headerIconSize;
                        }
                        tab.name.outerWidth(width);
                    }
                }
            }
        }

        if (this._param.tabsDropDown && hasTabsInHeader) {
            this.ensureDropDownVisibility();
            if (this.active) {
                this.focusAssistant.ensureTabVisibility(this.active);
            }
            this.ensureDropDownVisibility();
        }
    }

    public items(newItems: ITabItem[]): void {
        this.content.empty();
        this.header.empty();
        // for (const tab of this.tabs) {
        //     tab.destroy();
        // }
        this.tabs.length = 0;

        if (newItems && newItems.length > 0 && !_.find(newItems, tab => tab.isActive)) {
            newItems[0].isActive = true;
        }

        for (const item of newItems) {
            this.createTab(item);
        }

        if (this._param.tabsDropDown) {
            $('<li class="drop-down-tabs"/>').append(this.dropDownTabs.getControl().hide()).appendTo(this.header);
        }

        this.needVisibly();
    }

    /***@deprecated*/
    public setError(msg: string): void;
    public setError(tabId: string, msg: string): void;
    public setError(tabId: string, msg?: string): void {
        const tab = this.getTab(tabId);
        if (tab) {
            tab.setError(msg);
            this.adjustHeader();
        }
    }

    /***@deprecated*/
    public removeError(): void;
    public removeError(tabId: string): void;
    public removeError(tabId?: string): void {
        const tab = this.getTab(tabId);
        if (tab) {
            tab.removeError();
            this.adjustHeader();
        }
    }

    public showTab(tabId: string): void {
        const tab = this.getTab(tabId);
        if (tab) {
            this.tabVisibility(tab, true);
        }
    }

    public hideTab(tabId: string): void {
        const tab = this.getTab(tabId);
        if (tab) {
            this.tabVisibility(tab, false);
        }
    }

    public addTab(data: ITabItem, index?: number): void {
        this.createTab(data, { addedTab: true, index });
        this.needVisibly();
    }

    public destroy(): void {
        safeDestroys(...this.tabs.map(tab => tab.content));
        this.tabs.length = 0;
        safeDestroys(this.dropDownTabs, this.header);
        super.destroy();
    }

    private tabVisibility(tab: TabItem, visibility: boolean): void {
        tab.visibility(visibility);
        const ddNode = this.dropDownTabs.findNodeById(tab.id);
        ddNode?.visibility(visibility);
    }

    private needVisibly(): void {
        this._root.css('visibility', this.tabs.length > 0 ? 'visible' : 'hidden');
    }

    private ensureDropDownVisibility(): void {
        const header = this.header[0] as HTMLUListElement;
        if (header.scrollWidth > header.clientWidth || this.focusAssistant.isActive) {
            this.dropDownTabs.getControl().show();
            this.header.addClass('has-dropdown');
        } else {
            this.dropDownTabs.getControl().hide();
            this.header.removeClass('has-dropdown');
        }
    }
}
